import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import { StyledModalBody, StyledModalTitle } from "../../components/Modals";
import { Form, FormControl, FormField, FormItem, FormMessage } from "../../components/ui/Form";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Separator } from "../../components/ui/Separator";
import { Fonts, Palette } from "../../components/utils";
import { Flex } from "../../components/ui/Flex";
import { FeatureSwitch } from "../offers/styles";
import { ButtonBodyVariant } from "../../components/ui/ButtonVariant";
import { CModalHeader } from "@coreui/react";
import { ModalCloseButton } from "../settings/SettingsButton";
import { StyledBadge } from "../../components/ui/Badge";
import { closeActiveModal, openModal } from "../../helper/modals";
import { ReactComponent as StatusIcon } from "../../assets/icons/Status_Icon.svg";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { FaRegCircle } from "react-icons/fa";
import { useForm } from "react-hook-form";
import { SelectItem } from "../../components/ui/Select";
import { 
  attachThermostatPreset, 
  searchThermostats,
  getBuildingThermostatPresets,
  createThermostatPreset,
  updateBuildingThermostatPresets,
  updateThermostatBulk,
  updateThermostatHvacModeBulk
} from "../../redux/actions/thermostat";
import { setToast } from "../../redux/actions/window";
import { SnapBackTemperatureControl, StyledLabel, StyledSelect, TemperatureRangeControl, ThermostatModal, TitleText, SetPointRefreshFrequencyControl } from "./ThermostatComponents";
import { createThermostatSchema } from "./ThermostatValidations";
import { SettingsModalInput } from "../settings/SettingsModals";
import { requestPending, requestSuccess, requestFail } from "../../utils/status";
import { GET_BUILDING_THERMOSTAT_PRESETS, UPDATE_BUILDING_THERMOSTAT_PRESETS, CREATE_THERMOSTAT_PRESET } from "../../redux/constants";
import { defaultSetPoint, defaultSetPointCelsius, defaultTemperatureInputRanges, defaultTemperatureRange, defaultTemperatureInputRangesCelsius, defaultTemperatureRangeCelsius } from "../../utils/utils";
import _ from "lodash";
import { yupResolver } from "@hookform/resolvers/yup";

export const ThermostatControlModal = () => {
  const dispatch = useDispatch();
  const { modal, data } = useSelector((state) => state.headerModal);
  const thermostatData = useSelector((state) => state.thermostat.thermostatData);
  const thermostat = useSelector((state) => state.thermostat);
  const organization = useSelector((state) => state.Organization.organization);
  const globalBuildingId = useSelector((state) => state.building.globalBuildingId);
  const globalSelectedBuilding = useSelector((state) => state.building.globalSelectedBuilding);
  const buildingPresets = useSelector((state) => state.thermostat.buildingPresets);
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState('custom');
  const [page, setPage] = useState(1);
  const [submittedForm, setSubmittedForm] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const buildingTempUnit = globalSelectedBuilding?.config?.thermostats_temp_unit || 'F';

  const {
    buildingPresetsStatus,
    updatePresetsStatus,
    createPresetStatus,
  } = useSelector((state) => state.thermostat);

  const { globalOrgId } = useSelector((state) => state.Organization);
  const { globalBuildingId: globalBuildingIdFromState } = useSelector((state) => state.building);

  const organizationId = globalOrgId || localStorage.getItem("orgId");
  const buildingId = globalBuildingId || globalBuildingIdFromState || localStorage.getItem("building_id");

  const isUpdatingPresets = updatePresetsStatus === requestPending(UPDATE_BUILDING_THERMOSTAT_PRESETS);
  const isCreatingPreset = createPresetStatus === requestPending(CREATE_THERMOSTAT_PRESET);
  const hasPresetsLoaded = buildingPresetsStatus === requestSuccess(GET_BUILDING_THERMOSTAT_PRESETS);

  useEffect(() => {
    if (organizationId && buildingId) {
      dispatch(getBuildingThermostatPresets({ organizationId, buildingId }));
    }
  }, [dispatch, organizationId, buildingId]);

  const defaultRanges = buildingTempUnit === 'C' ? defaultTemperatureInputRangesCelsius : defaultTemperatureInputRanges;
  const selectedThermostatIds = Array.from(data.map.keys());

  const form = useForm({
    resolver: yupResolver(createThermostatSchema(buildingTempUnit)),
    mode: 'onSubmit',
    defaultValues: {
      mode: 'custom',
      enableSnapBack: false,
      snapBackTemperature: buildingTempUnit === 'C' ? defaultSetPointCelsius.occupied : defaultSetPoint.occupied,
      autoTemperatureAdjustment: 3,
      heatTemperatureRange: defaultRanges.heat,
      coolTemperatureRange: defaultRanges.cool,
      hvacMode: undefined,
      fanMode: undefined,
      hold: undefined,
      heatHoldTemperature: undefined,
      coolHoldTemperature: undefined,
    }
  });

  const enableSnapBackWatch = form.watch('enableSnapBack');
  const hvacModeWatch = form.watch('hvacMode')?.toLowerCase();
  const modeWatch = form.watch('mode');
  const heatRange = form.watch('heatTemperatureRange');
  const coolRange = form.watch('coolTemperatureRange');
  
  // Get temperature ranges from selected preset, fallback to form values
  const presetHeatRange = selectedPreset ? [
    selectedPreset.min_heat ?? heatRange[0],
    selectedPreset.max_heat ?? heatRange[1]
  ] : heatRange;
  
  const presetCoolRange = selectedPreset ? [
    selectedPreset.min_cool ?? coolRange[0],
    selectedPreset.max_cool ?? coolRange[1]
  ] : coolRange;

  // Calculate min/max ranges based on HVAC mode
  const { currentMin, currentMax } = useMemo(() => {
    const heatRangeToUse = modeWatch === 'custom' ? heatRange : presetHeatRange;
    const coolRangeToUse = modeWatch === 'custom' ? coolRange : presetCoolRange;
    if (hvacModeWatch === 'heat') {
      return {
        currentMin: heatRangeToUse[0],
        currentMax: heatRangeToUse[1]
      };
    } else if (hvacModeWatch === 'cool') {
      return {
        currentMin: coolRangeToUse[0],
        currentMax: coolRangeToUse[1]
      };
    } else {
      return {
        currentMin: heatRangeToUse[0],
        currentMax: coolRangeToUse[1]
      };
    }
  }, [hvacModeWatch, heatRange, coolRange, presetHeatRange, presetCoolRange]);

  const isFormValid = useMemo(() => {
    const hasManualErrors = Object.keys(form.formState.errors).length > 0;
    return !hasManualErrors;
  }, [form.formState.errors]);

  useEffect(() => {
    if (!hasPresetsLoaded || buildingPresets?.length === 0) {
      return;
    }

    const preset = buildingPresets.find(p => p.type === modeWatch);
    const isC = buildingTempUnit === 'C';
    const defaultSnapBack = isC 
      ? (preset ? defaultSetPointCelsius.occupied : defaultSetPointCelsius.custom)
      : (preset ? defaultSetPoint.occupied : defaultSetPoint.custom);
    
    const defaultHeatRange = isC 
      ? defaultTemperatureInputRangesCelsius.heat
      : defaultTemperatureInputRanges.heat;
    const defaultCoolRange = isC 
      ? defaultTemperatureInputRangesCelsius.cool
      : defaultTemperatureInputRanges.cool;
  
    let finalSnapBackTemp;
    let finalHeatRange;
    let finalCoolRange;
    let finalSnapBackEnabled = false;
    let finalRefreshFreq = 3;
  
    if (preset) {
      const p = preset;
  
      finalSnapBackTemp = p.snapback_setpoint ?? defaultSnapBack;
      
      finalHeatRange = [
        p.min_heat ?? defaultHeatRange[0],
        p.max_heat ?? defaultHeatRange[1],
      ];
      finalCoolRange = [
        p.min_cool ?? defaultCoolRange[0],
        p.max_cool ?? defaultCoolRange[1],
      ];
  
      finalSnapBackEnabled = p.snapback_enabled ?? false;
      finalRefreshFreq = Number(p.snapback_refresh_frequency_hours) || 3;
      
      setSelectedPreset(preset);
  
    } else {
      setSelectedPreset({ type: 'custom' });
      finalSnapBackTemp = defaultSnapBack;
      finalHeatRange = defaultHeatRange;
      finalCoolRange = defaultCoolRange;
    }
  
    form.setValue('enableSnapBack', finalSnapBackEnabled);
    form.setValue('snapBackTemperature', finalSnapBackTemp); 
    form.setValue('autoTemperatureAdjustment', finalRefreshFreq);
    form.setValue('heatTemperatureRange', finalHeatRange);
    form.setValue('coolTemperatureRange', finalCoolRange);

    return () => {
      form.reset(form.getValues());
    }
    
  }, [hasPresetsLoaded, buildingPresets, form, buildingTempUnit, modeWatch]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name && ['hvacMode', 'fanMode', 'hold', 'heatHoldTemperature', 'coolHoldTemperature','snapBackTemperature'].includes(name)) {
        form.trigger(name);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = (values) => {
    if (page === 1) {
      const validationErrors = validateThermostatInputs(values, values.hvacMode?.toLowerCase());
      
      if (Object.keys(validationErrors).length > 0) {
        Object.keys(validationErrors).forEach(field => {
          form.setError(field, { message: validationErrors[field] });
        });
        return;
      }
      
      setPage(2);
    } else {
      saveChanges(values);
    }
  };

  const handleCancel = () => {
    if (page === 2) {
      form.clearErrors('hold');
      form.clearErrors('heatHoldTemperature');
      form.clearErrors('coolHoldTemperature');
      form.clearErrors('hvacMode');
      form.clearErrors('fanMode');
      
      form.setValue('hold', undefined, { shouldDirty: false });
      form.setValue('heatHoldTemperature', undefined, { shouldDirty: false });
      form.setValue('coolHoldTemperature', undefined, { shouldDirty: false });
      form.setValue('hvacMode', undefined, { shouldDirty: false });
      form.setValue('fanMode', undefined, { shouldDirty: false });
      
      setPage(1);
    } else {
      closeActiveModal();
    }
  };


  const getCanonicalPresetState = (preset, defaultRanges) => {
    return {
      snapback_setpoint: preset.snapback_setpoint,
      snapback_refresh_frequency_hours: preset.snapback_refresh_frequency_hours,
      min_max_enabled: preset.min_max_enabled,
      min_heat: preset.min_heat,
      max_heat: preset.max_heat,
      min_cool: preset.min_cool,
      max_cool: preset.max_cool,
    };
  };
  
  const getFormPresetState = (values) => ({
    snapback_setpoint: values.snapBackTemperature,
    snapback_refresh_frequency_hours: values.autoTemperatureAdjustment,
    min_max_enabled: true,
    min_heat: values.heatTemperatureRange[0],
    max_heat: values.heatTemperatureRange[1],
    min_cool: values.coolTemperatureRange[0],
    max_cool: values.coolTemperatureRange[1],
  });

  const validateThermostatInputs = (values, hvacMode) => {
    const errors = {};
    const hasHvacInput = values.hvacMode;
    const hasFanInput = values.fanMode;
    const hasTempInput = values.hold || values.heatHoldTemperature || values.coolHoldTemperature;
    
    const hasAnyAdditionalSettings = hasHvacInput || hasFanInput || hasTempInput;
    if (hasAnyAdditionalSettings) {
      if (!hasHvacInput) {
        errors.hvacMode = t('HVAC mode is required when updating additional settings');
      }
      
      if (!hasFanInput) {
        errors.fanMode = t('Fan mode is required when updating additional settings');
      }
      
      if (!hasTempInput) {
        if (hvacMode === 'auto') {
          errors.heatHoldTemperature = t('Heat hold temperature is required when updating additional settings');
          errors.coolHoldTemperature = t('Cool hold temperature is required when updating additional settings');
        } else {
          errors.hold = t('Temperature is required when updating additional settings');
        }
      }
      
      if (hasTempInput && hvacMode === 'auto') {
        if (!values.heatHoldTemperature || !values.coolHoldTemperature) {
          errors.heatHoldTemperature = t('Heat hold temperature is required for Auto mode');
          errors.coolHoldTemperature = t('Cool hold temperature is required for Auto mode');
        }
      }
      
      if (values.hold !== undefined) {
        const heatRangeToUse = modeWatch === 'custom' ? heatRange : presetHeatRange;
        const coolRangeToUse = modeWatch === 'custom' ? coolRange : presetCoolRange;
        let currentMin, currentMax;
        
        if (hvacMode === 'heat') {
          currentMin = heatRangeToUse[0];
          currentMax = heatRangeToUse[1];
        } else if (hvacMode === 'cool') {
          currentMin = coolRangeToUse[0];
          currentMax = coolRangeToUse[1];
        } else {
          currentMin = heatRangeToUse[0];
          currentMax = coolRangeToUse[1];
        }
        
        if (values.hold < currentMin || values.hold > currentMax) {
          errors.hold = t(`Temperature must be within ${currentMin}°${buildingTempUnit} - ${currentMax}°${buildingTempUnit}`);
        }
      }
      
      if (values.heatHoldTemperature !== undefined && (values.heatHoldTemperature < presetHeatRange[0] || values.heatHoldTemperature > presetHeatRange[1])) {
        errors.heatHoldTemperature = t(`Heat temperature must be within ${presetHeatRange[0]}°${buildingTempUnit} - ${presetHeatRange[1]}°${buildingTempUnit}`);
      }
      
      if (values.coolHoldTemperature !== undefined && (values.coolHoldTemperature < presetCoolRange[0] || values.coolHoldTemperature > presetCoolRange[1])) {
        errors.coolHoldTemperature = t(`Cool temperature must be within ${presetCoolRange[0]}°${buildingTempUnit} - ${presetCoolRange[1]}°${buildingTempUnit}`);
      }
    }
    
    return errors;
  };

  const updateTemperatureAndFanSettings = useCallback(async (selectedThermostats, values, hvacMode, fanMode) => {
    const tempFanData = [];
    
    for (const thermostat of selectedThermostats) {
      const holdValue = values.hold ? parseInt(values.hold) : null;
      let finalHeatTemp, finalCoolTemp;
      
      if (hvacMode === 'auto') {
        finalHeatTemp = values.heatHoldTemperature !== undefined ? values.heatHoldTemperature : thermostat.heatHoldTemperature;
        finalCoolTemp = values.coolHoldTemperature !== undefined ? values.coolHoldTemperature : thermostat.coolHoldTemperature;
      } else {
        finalHeatTemp = holdValue !== null ? holdValue : thermostat.temperature;
        finalCoolTemp = holdValue !== null ? holdValue : thermostat.temperature;
      }
      
      tempFanData.push({
        thermostat_id: thermostat.id,
        hvac_mode: hvacMode,
        cool_temperature: Number(finalCoolTemp),
        heat_temperature: Number(finalHeatTemp),
        temperature_duration: thermostat.temperature_duration || 'indefinite',
        fan: fanMode,
        fan_duration: thermostat.fan_duration || 'indefinite',
      });
    }
    
    if (tempFanData.length > 0) {
      await dispatch(updateThermostatBulk({
        body: tempFanData,
        success: () => dispatch(setToast({ toastShow: true, toastMessage: t('Thermostat settings have been successfully updated') })),
        fail: (error) => {
          dispatch(setToast({ toastShow: true, toastMessage: t('Failed to update thermostat settings'), modal: 'errorToast' }));
        },
      }));
    }
  }, [dispatch, t]);

  const saveChanges = useCallback(async () => {
    setSubmittedForm(true);
    const values = form.getValues();
    try {
      let presetIdToAttach = null;
      
      if (values.mode.toLowerCase() === 'occupied' || values.mode.toLowerCase() === 'vacant') {
        const preset = buildingPresets.find(p => p.type === values.mode.toLowerCase());
        if (preset) {
          presetIdToAttach = preset.id;
        }
      }
    
      else if (values.mode.toLowerCase() === 'custom') {
        const existingCustomPreset = buildingPresets.find(p => p.type === 'custom');    
        if (existingCustomPreset) {
          const presetPayload = {
            type: 'custom',
            snapback_enabled: values.enableSnapBack,
            snapback_setpoint: values.snapBackTemperature,
            snapback_refresh_frequency_hours: values.autoTemperatureAdjustment,
            min_max_enabled: true,
            min_heat: values.heatTemperatureRange[0],
            max_heat: values.heatTemperatureRange[1],
            min_cool: values.coolTemperatureRange[0],
            max_cool: values.coolTemperatureRange[1],
            org_id: parseInt(organizationId),
            building_id: parseInt(buildingId),
          };
          
          const formState = getFormPresetState(values);
          const existingState = getCanonicalPresetState(existingCustomPreset, defaultTemperatureInputRanges);
          const hasChanges = !_.isEqual(formState, existingState);
          
          if (hasChanges) {
            // Update the existing custom preset
            await dispatch(updateBuildingThermostatPresets({
              organizationId: parseInt(organizationId),
              buildingId: parseInt(buildingId),
              body: [{ ...presetPayload, id: existingCustomPreset.id }],
              success: () => dispatch(setToast({ toastShow: true, toastMessage: t('Custom preset has been successfully updated') })),
              fail: (error) => {
                dispatch(setToast({ toastShow: true, toastMessage: t('Failed to update custom preset'), modal: 'errorToast' }));
              },
            }));
          }
          
          presetIdToAttach = existingCustomPreset.id;
        } else {
          // No existing custom preset - create new one
          const presetPayload = {
            type: 'custom',
            snapback_enabled: values.enableSnapBack,
            snapback_setpoint: values.snapBackTemperature,
            snapback_refresh_frequency_hours: values.autoTemperatureAdjustment,
            min_max_enabled: true,
            min_heat: values.heatTemperatureRange[0],
            max_heat: values.heatTemperatureRange[1],
            min_cool: values.coolTemperatureRange[0],
            max_cool: values.coolTemperatureRange[1],
            org_id: parseInt(organizationId),
            building_id: parseInt(buildingId),
          };
          
          await dispatch(createThermostatPreset({
            organizationId: parseInt(organizationId),
            buildingId: parseInt(buildingId),
            body: presetPayload,
            success: async (response) => {
              dispatch(setToast({ toastShow: true, toastMessage: t('Custom preset has been successfully created') }));
              
              // Attach the newly created preset to thermostats
              await dispatch(attachThermostatPreset({
                presetId: parseInt(response.data.id),
                body: { orgId: organizationId, buildingId, deviceIds: selectedThermostatIds },
                success: () => dispatch(setToast({ toastShow: true, toastMessage: t('Preset has been successfully attached') })),
                fail: () => dispatch(setToast({ toastShow: true, toastMessage: t('Failed to attach preset'), modal: 'errorToast' })),
              }));
            },
            fail: (error) => {
              dispatch(setToast({ toastShow: true, toastMessage: t('Failed to create custom preset'), modal: 'errorToast' }));
            }
          }));
          
          // Skip the attach preset logic below since it's now handled in the success callback
          presetIdToAttach = null;
        }
      }
      
      // 3. Attach the preset to thermostats (only for occupied/vacant presets, custom preset attachment is handled in its success callback)
      if (presetIdToAttach) {
        await dispatch(attachThermostatPreset({
          presetId: parseInt(presetIdToAttach),
          body: { orgId: organizationId, buildingId, deviceIds: selectedThermostatIds },
          success: () => dispatch(setToast({ toastShow: true, toastMessage: t('Preset has been successfully attached') })),
          fail: () => dispatch(setToast({ toastShow: true, toastMessage: t('Failed to attach preset'), modal: 'errorToast' })),
        }));
      }
      
      // 4. Handle thermostat settings updates (HVAC mode, temperature, fan)
      const selectedThermostats = thermostatData.filter(thermostat => selectedThermostatIds.includes(thermostat.id));
      
      // Check if we have additional settings (all-or-nothing validation ensures we have all 3 or none)
      const hasAdditionalSettings = values.hvacMode || values.fanMode || values.hold || values.heatHoldTemperature || values.coolHoldTemperature;
      
      if (hasAdditionalSettings) {
        const hvacMode = values.hvacMode?.toLowerCase();
        const fanMode = values.fanMode?.toLowerCase();
        
        // First, update HVAC mode if needed
        const hvacModeBody = selectedThermostats
          .filter(t => hvacMode && t.hvacMode?.toLowerCase() !== hvacMode)
          .map(t => ({ thermostat_id: t.id, hvacMode }));
        
        if (hvacModeBody.length > 0) {
          await dispatch(updateThermostatHvacModeBulk({
            body: hvacModeBody,
            success: async () => {
              // After HVAC mode is updated, update temperature and fan settings
              await updateTemperatureAndFanSettings(selectedThermostats, values, hvacMode, fanMode);
            },
            fail: (error) => {
              dispatch(setToast({ toastShow: true, toastMessage: t('Failed to update HVAC mode'), modal: 'errorToast' }));
            },
          }));
        } else {
          // No HVAC mode change needed, just update temperature and fan
          await updateTemperatureAndFanSettings(selectedThermostats, values, hvacMode, fanMode);
        }
      }
      
      // 6. Refresh and close
      dispatch(searchThermostats({ orgID: organizationId, buildingID: globalBuildingId, all: true }));
      closeActiveModal();
      
    } catch (error) {
      console.error('Error in saveChanges:', error);
    }
  }, [dispatch, form, data, selectedPreset, organizationId, buildingId, organization, globalBuildingId, thermostatData, t, buildingTempUnit, buildingPresets]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <ThermostatModal
          show={modal === 'ThermostatControlModal'}
          side
          closeOnBackdrop={false}>
          <div style={{ padding: '1.5rem' }}>

            <CModalHeader>
              <StyledModalTitle>Thermostat Control</StyledModalTitle>
              <Flex className="ml-auto" alignItems="center" justify="center" gap="8px">
                <StyledBadge variant="active">
                  {data.map.size} Selected
                </StyledBadge>
                <ModalCloseButton onCloseCallback={() => closeActiveModal()} />
              </Flex>
            </CModalHeader>
          </div>

          <Separator color={Palette.BLACK_20} orientation="horizontal" />

          {page === 1 && <StyledModalBody>
            <Flex gap="1rem" direction="column" style={{ padding: '0.5rem 1.5rem 1.5rem' }}>

              <div>
                <TitleText>
                  Select Mode
                </TitleText>
                <Text>
                  Forces a fixed temperature range for all units when enabled.
                </Text>
              </div>

              <Flex justify="between" alignItems="center" gap="0.5rem">
                <ModeButton type="button" onClick={() => {
                  setTab('custom')
                  form.setValue('mode', 'custom', { shouldDirty: true });
                  const customPreset = buildingPresets?.find(p => p.type === 'custom');
                  setSelectedPreset(customPreset || { type: 'custom' });
                }} isActive={tab === 'custom'}>
                  <Flex justify="center" alignItems="center" gap="0.75rem">
                    {tab === 'custom' ? <IoIosCheckmarkCircle size={20} color="black" /> : <FaRegCircle size={17} color="black" />}
                    <ModeButtonText>
                      Custom
                    </ModeButtonText>
                  </Flex>
                </ModeButton>

                <ModeButton type="button" onClick={() => {
                  setTab('vacant')
                  form.setValue('mode', 'vacant', { shouldDirty: true })
                  const vacantPreset = buildingPresets?.find(p => p.type === 'vacant');
                  setSelectedPreset(vacantPreset || { type: 'vacant' });
                }} isActive={tab === 'vacant'}>
                  <Flex justify="center" alignItems="center" gap="0.75rem">
                    {tab === 'vacant' ? <IoIosCheckmarkCircle size={20} color="black" /> : <FaRegCircle size={17} color="black" />}
                    <ModeButtonText>
                      Vacant
                    </ModeButtonText>
                  </Flex>
                </ModeButton>

                <ModeButton type="button" onClick={() => {
                  setTab('occupied')
                  form.setValue('mode', 'occupied', { shouldDirty: true })
                  const occupiedPreset = buildingPresets?.find(p => p.type === 'occupied');
                  setSelectedPreset(occupiedPreset || { type: 'occupied' });
                }} isActive={tab === 'occupied'}>
                  <Flex justify="center" alignItems="center" gap="0.75rem">
                    {tab === 'occupied' ? <IoIosCheckmarkCircle size={20} color="black" /> : <FaRegCircle size={17} color="black" />}
                    <ModeButtonText>
                      Occupied
                    </ModeButtonText>
                  </Flex>
                </ModeButton>
              </Flex>

              <Separator color={Palette.BLACK_20} orientation="horizontal" />

              {tab === 'custom' ? <>

                <Flex gap="3rem" justify="space-between">
                  <Flex direction="column" gap="4px" >
                    <SubTitleText>
                      Snap Back Temperature
                    </SubTitleText>
                    <Text>
                      Returns the thermostat to a preset temperature range after manual changes or periods of inactivity.
                    </Text>
                  </Flex>
                  <FormField
                    control={form.control}
                    name="enableSnapBack"
                    render={({ field: { onChange, value } }) => (
                      <FeatureSwitch
                        onChange={onChange}
                        checked={value}
                        value={value}
                        shape="pill"
                        color="primary"
                      />
                    )}
                  />
                </Flex>

                {enableSnapBackWatch && (
                  <>
                    <SnapBackTemperatureControl 
                      name="snapBackTemperature" 
                      control={form.control} 
                      buildingTempUnit={buildingTempUnit}
                    />
                    <SetPointRefreshFrequencyControl
                      name="autoTemperatureAdjustment"
                      control={form.control}
                    />
                  </>
                )}

                <Separator color={Palette.BLACK_20} orientation="horizontal" />

                <Flex direction="column" gap="1rem" style={{ width: "100%" }}>
                  <Flex direction="column" gap="4px" >
                    <SubTitleText>
                      Set Min & Max Temperature
                    </SubTitleText>
                    <Text>
                      Set the minimum and maximum temperature that can be adjusted for this thermostat under Heating and Cooling mode.
                    </Text>
                  </Flex>

                  <Flex direction="column" gap="0.5rem" style={{ width: "100%" }}>
                    <TemperatureRangeControl 
                      name="heatTemperatureRange" 
                      label="Heat" 
                      minMax={buildingTempUnit === 'C' ? defaultTemperatureRangeCelsius.heat : defaultTemperatureRange.heat} 
                      control={form.control} 
                      buildingTempUnit={buildingTempUnit}
                    />
                    <TemperatureRangeControl 
                      name="coolTemperatureRange" 
                      label="Cool" 
                      minMax={buildingTempUnit === 'C' ? defaultTemperatureRangeCelsius.cool : defaultTemperatureRange.cool} 
                      control={form.control} 
                      buildingTempUnit={buildingTempUnit}
                    />
                  </Flex>
                </Flex>
              </>
                :
                <>
                 <Text style={{ display: 'inline-block' }} normal>
                  With <Text bold italic style={{ display: 'inline-block' }}>Min & Max Temperature</Text> enabled,
                  residents can adjust their thermostats within the following range.{' '}
                  <Text bold italic style={{ display: 'inline-block' }}>Heat -</Text>{' '}
                  <Text bold italic style={{ display: 'inline-block' }}>{selectedPreset?.min_heat ?? heatRange[0]}°{buildingTempUnit} to {selectedPreset?.max_heat ?? heatRange[1]}°{buildingTempUnit}</Text>{' '}
                  <Text bold italic style={{ display: 'inline-block' }}>Cool -</Text>{' '}
                  <Text bold italic style={{ display: 'inline-block' }}>{selectedPreset?.min_cool ?? coolRange[0]}°{buildingTempUnit} to {selectedPreset?.max_cool ?? coolRange[1]}°{buildingTempUnit}.</Text>{' '}
                  <Text normal style={{ display: 'inline-block' }}>These settings can be updated at any time in <GlobalSettingsText onClick={() => openModal('GlobalThermostatControlModal')}>Global Settings.</GlobalSettingsText></Text>
                </Text>
                </>
              }

              <Separator color={Palette.BLACK_20} orientation="horizontal" />

              <Flex direction="column" gap="4px" >
                <SubTitleText>
                  Advanced Temperature Controls
                </SubTitleText>
                <Text>
                  Control HVAC mode, fan settings, and hold behavior at the unit level.
                </Text>
              </Flex>

              <>
                <FormField
                  control={form.control}
                  name="hvacMode"
                  render={({ field }) => (
                    <FormItem alt>
                      <StyledLabel>HVAC Mode</StyledLabel>
                      <FormControl>
                        <StyledSelect
                          {...field}
                          placeholder="Select"
                          height="3rem"
                        >
                          <SelectItem value={undefined}>Select</SelectItem>
                          <SelectItem value={'Heat'}>Heat</SelectItem>
                          <SelectItem value={'Cool'}>Cool</SelectItem>
                          <SelectItem value={'Auto'}>Auto</SelectItem>
                          <SelectItem value={'Off'}>Off</SelectItem>
                        </StyledSelect>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fanMode"
                  render={({ field }) => (
                    <FormItem alt>
                      <StyledLabel>Fan Mode</StyledLabel>
                      <FormControl>
                        <StyledSelect
                          {...field}
                          placeholder="Select"
                          height="3rem"
                        >
                          <SelectItem value={undefined}>Select</SelectItem>
                          <SelectItem value={'On'}>On</SelectItem>
                          <SelectItem value={'Auto'}>Auto</SelectItem>
                        </StyledSelect>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {hvacModeWatch !== 'auto' ? (
                  <FormField
                  control={form.control}
                  name="hold"
                  render={({ field }) => (
                    <FormItem alt>
                      <StyledLabel>{t('Set Temp')}</StyledLabel>
                      <FormControl>
                        <SettingsModalInput
                          type="number"
                          placeholder={t(`Enter desired temperature (${currentMin}°${buildingTempUnit} - ${currentMax}°${buildingTempUnit})`)}
                          autoComplete="off"
                          min={currentMin}
                          max={currentMax}
                          cssOverride={`
                            height: 3rem;
                            color: ${Palette.CONTENT_TERTIARY};
                            font-family: ${Fonts.OPEN_SANS_FONT};
                            font-size: 0.875rem;
                            font-style: normal;
                            font-weight: 400;
                            line-height: 1.5rem;
                            letter-spacing: 0rem;
                          `}
                          {...field}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                          
                            form.clearErrors('hold');
                            if (inputValue === '' || inputValue === null || inputValue === undefined) {
                              field.onChange(undefined);
                              return;
                            }
                            
                            const value = parseFloat(inputValue);
                            if (isNaN(value)) {
                              field.onChange(undefined);
                              return;
                            }
                            
                            field.onChange(value);
                          
                            if (value < currentMin || value > currentMax) {
                              form.setError('hold', {
                                type: 'manual',
                                message: t(`Temperature must be within ${currentMin}°${buildingTempUnit} - ${currentMax}°${buildingTempUnit}`)
                              });
                            }
                            
                            form.trigger('hold');
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                  />
                ) : (
                  <>
                      <FormField
                      control={form.control}
                      name="coolHoldTemperature"
                      render={({ field, onChange, value }) => (
                        <FormItem alt>
                          <StyledLabel>{t('Set Cool Temp')}</StyledLabel>
                          <FormControl>
                            <SettingsModalInput
                              type="number"
                              placeholder={t(`Enter desired temperature (${presetCoolRange[0] + '°' + buildingTempUnit + ' - ' + presetCoolRange[1] + '°' + buildingTempUnit})`)}
                              autoComplete="off"
                              min={presetCoolRange[0]}
                              max={presetCoolRange[1]}
                              cssOverride={`
                                height: 3rem;
                                color: ${Palette.CONTENT_TERTIARY};
                                font-family: ${Fonts.OPEN_SANS_FONT};
                                font-size: 0.875rem;
                                font-style: normal;
                                font-weight: 400;
                                line-height: 1.5rem;
                                letter-spacing: 0rem;
                              `}
                              {...field}
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                form.clearErrors('coolHoldTemperature');
                                
                                if (inputValue === '' || inputValue === null || inputValue === undefined) {
                                  field.onChange(undefined);
                                  return;
                                }
                                const value = parseFloat(inputValue);
                                if (isNaN(value)) {
                                  field.onChange(undefined);
                                  return;
                                }
                                
                                field.onChange(value);
                                
                                if (value < presetCoolRange[0] || value > presetCoolRange[1]) {
                                  form.setError('coolHoldTemperature', {
                                    type: 'manual',
                                    message: t(`Temperature must be within ${presetCoolRange[0]}°${buildingTempUnit} - ${presetCoolRange[1]}°${buildingTempUnit}`)
                                  });
                                }
                                
                                form.trigger('coolHoldTemperature');
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                      />
                  <FormField
                  control={form.control}
                  name="heatHoldTemperature"
                  render={({ field, onChange, value }) => (
                    <FormItem alt>
                      <StyledLabel>{t('Set Heat Temp')}</StyledLabel>
                      <FormControl>
                        <SettingsModalInput
                          type="number"
                          placeholder={t(`Enter desired temperature (${presetHeatRange[0] + '°' + buildingTempUnit + ' - ' + presetHeatRange[1] + '°' + buildingTempUnit})`)}
                          autoComplete="off"
                          min={presetHeatRange[0]}
                          max={presetHeatRange[1]}
                          cssOverride={`
                            height: 3rem;
                            color: ${Palette.CONTENT_TERTIARY};
                            font-family: ${Fonts.OPEN_SANS_FONT};
                            font-size: 0.875rem;
                            font-style: normal;
                            font-weight: 400;
                            line-height: 1.5rem;
                            letter-spacing: 0rem;
                          `}
                          {...field}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            form.clearErrors('heatHoldTemperature');
                            
                            if (inputValue === '' || inputValue === null || inputValue === undefined) {
                              field.onChange(undefined);
                              return;
                            }
                            
                            const value = parseFloat(inputValue);
                            if (isNaN(value)) {
                              field.onChange(undefined);
                              return;
                            }

                            field.onChange(value);

                            if (value < presetHeatRange[0] || value > presetHeatRange[1]) {
                              form.setError('heatHoldTemperature', {
                                type: 'manual',
                                message: t(`Temperature must be within ${presetHeatRange[0]}°${buildingTempUnit} - ${presetHeatRange[1]}°${buildingTempUnit}`)
                              });
                            }
                            
                            field.onChange(value);
                            form.trigger('heatHoldTemperature');
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                  />
                  </>
                )}
              </>
            </Flex>

          </StyledModalBody>}


          {page === 2 && <StyledModalBody>
            <Flex gap="1.5rem" direction="column" style={{ padding: '2.5rem' }} justify="center" alignItems="center">
              <Flex direction="column" justify="center" alignItems="center" gap="1rem">
                <StatusIcon style={{ width: '4.6rem', height: '4.6rem' }} />

                <Flex direction="column" justify="center" alignItems="center" gap="0.25rem">

                  <TitleText style={{ fontSize: '1.5rem', color: Palette.BLACK, lineHeight: '2.5rem' }}>
                    Confirmation
                  </TitleText>

                  <Text style={{ textAlign: 'center', fontSize: '1rem', color: Palette.BLACK, lineHeight: '1.5rem' }}>
                    You are going to update the thermostat settings for this building.
                  </Text>
                </Flex>
              </Flex>

              <Flex direction="column" justify="center" alignItems="center" gap="1.50rem">
                <Flex direction="column" justify="center" alignItems="center" gap="0.25rem">
                  <SubTitleText alt>
                    {data.map.size} Unit{data.map.size > 1 && 's'}
                  </SubTitleText>
                  <Text style={{ textAlign: 'center' }} alt>
                    {Array.from(data.map.values()).map((unit) => unit.unitNum).join(', ')}
                  </Text>
                </Flex>

                <Flex direction="column" justify="center" alignItems="center" gap="0.25rem">
                  <SubTitleText alt>
                    Mode
                  </SubTitleText>
                  <Text style={{ textAlign: 'center' }} alt>
                    {form.getValues('mode')?.charAt(0).toUpperCase() + form.getValues('mode')?.slice(1)}
                  </Text>
                </Flex>


                {(form.getValues('hvacMode') || form.getValues('fanMode') || form.getValues('hold') || form.getValues('heatHoldTemperature') || form.getValues('coolHoldTemperature')) &&
                  <Flex direction="column" justify="center" alignItems="center" gap="0.25rem">
                    <SubTitleText alt>
                      Advance Temp Controls
                    </SubTitleText>

                    {form.getValues('hvacMode') &&
                      <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                        HVAC <Text alt style={{ display: 'inline-block' }}>{form.getValues('hvacMode')}</Text>
                      </Text>
                    }

                    {form.getValues('fanMode') &&
                      <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                        Fan <Text alt style={{ display: 'inline-block' }}>{form.getValues('fanMode')}</Text>
                      </Text>
                    }

                    {form.getValues('hold') && form.getValues('hvacMode').toLowerCase() !== 'auto' &&
                      <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                        Set Temp <Text alt style={{ display: 'inline-block' }}>{form.getValues('hold')}°{buildingTempUnit}</Text>
                      </Text>
                    }
                    
                    {form.getValues('hvacMode').toLowerCase() === 'auto' && (form.getValues('heatHoldTemperature') || form.getValues('coolHoldTemperature')) &&
                      <>
                        {form.getValues('heatHoldTemperature') &&
                          <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                            Heat Hold <Text alt style={{ display: 'inline-block' }}>{form.getValues('heatHoldTemperature')}°{buildingTempUnit}</Text>
                          </Text>
                        }
                        {form.getValues('coolHoldTemperature') &&
                          <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                            Cool Hold <Text alt style={{ display: 'inline-block' }}>{form.getValues('coolHoldTemperature')}°{buildingTempUnit}</Text>
                          </Text>
                        }
                      </>
                    }

                  </Flex>
                }

                {(form.getValues('enableSnapBack')) &&
                  <>
                    <Flex direction="column" justify="center" alignItems="center" gap="0.25rem">
                      <SubTitleText alt>
                        Snap Back
                      </SubTitleText>
                      <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                        Snap Back Setpoint <Text alt style={{ display: 'inline-block' }}>{form.getValues('snapBackTemperature')}°{buildingTempUnit}</Text>
                      </Text>
                      <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                        Setpoint Refresh Frequency <Text alt style={{ display: 'inline-block' }}>{form.getValues('autoTemperatureAdjustment')}hrs</Text>
                      </Text>

                    </Flex>
                  </>
                }
                <Flex direction="column" justify="center" alignItems="center" gap="0.25rem">
                    <SubTitleText alt>
                      Min & Max Set Temperature
                    </SubTitleText>
                    {tab === 'custom' ?
                      <>
                        <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                          Cool <Text alt style={{ display: 'inline-block' }}>{form.getValues('coolTemperatureRange')[0]}°{buildingTempUnit} - {form.getValues('coolTemperatureRange')[1]}°{buildingTempUnit}</Text>
                        </Text>
                        <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                          Heat <Text alt style={{ display: 'inline-block' }}>{form.getValues('heatTemperatureRange')[0]}°{buildingTempUnit} - {form.getValues('heatTemperatureRange')[1]}°{buildingTempUnit}</Text>
                        </Text>
                      </>
                      : tab === 'vacant' ?
                        <>
                          <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                            Cool <Text alt style={{ display: 'inline-block' }}>{presetCoolRange[0]}°{buildingTempUnit} - {presetCoolRange[1]}°{buildingTempUnit}</Text>
                          </Text>
                          <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                            Heat <Text alt style={{ display: 'inline-block' }}>{presetHeatRange[0]}°{buildingTempUnit} - {presetHeatRange[1]}°{buildingTempUnit}</Text>
                          </Text>
                        </>
                        :
                        <>
                          <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                            Cool <Text alt style={{ display: 'inline-block' }}>{presetCoolRange[0]}°{buildingTempUnit} - {presetCoolRange[1]}°{buildingTempUnit}</Text>
                          </Text>
                          <Text bold style={{ textAlign: 'center', display: 'inline-block' }} alt>
                            Heat <Text alt style={{ display: 'inline-block' }}>{presetHeatRange[0]}°{buildingTempUnit} - {presetHeatRange[1]}°{buildingTempUnit}</Text>
                          </Text>
                        </>
                    }
                </Flex>
              </Flex>

            </Flex>
          </StyledModalBody>
          }

          <Flex direction="column" gap="0.5rem" style={{ padding: '1.5rem' }}>
            <ButtonBodyVariant variant="secondary" onClick={handleCancel} style={{ width: '100%' }} disabled={submittedForm || isCreatingPreset || isUpdatingPresets} type="button">Cancel</ButtonBodyVariant>
            <ButtonBodyVariant 
              type="submit" 
              disabled={submittedForm || isCreatingPreset || isUpdatingPresets || !isFormValid}
              style={{ width: '100%' }}
            >
              {submittedForm ? t('Updating...') : (page === 1 ? t('Next') : t('Update'))}
            </ButtonBodyVariant>
          </Flex>
        </ThermostatModal>
      </form >
    </Form >
  );
};

const GlobalSettingsText = styled.p`
  display: inline;
  color: #3776BD;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.5rem;

  :hover {
    cursor: pointer;
  }
`

const ModeButton = styled.button`
  display: flex;
  align-items: center;

  gap: 10px;
  border: 1px solid ${Palette.DISABLED};
  border-radius: 0.625rem;
  flex: 1 0 0;
  padding: 0.75rem 1.125rem;
  background: ${Palette.WHITE}; 

  ${(props) =>
    props.isActive
    && `
    background: ${Palette.SOLITUDE};
    border: 1px solid ${Palette.HAZY_BLUE};
    `}

  &:focus {
    outline: none;
  }
`;

const ModeButtonText = styled.p`
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
  margin: 0;
`;

const SubTitleText = styled.p`
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.5rem;
  letter-spacing: 0rem;
  margin: 0;

  ${({ alt }) =>
    alt &&
    `
    color: ${Palette.CONTENT_TERTIARY};
  `}
`;

const Text = styled.p`
  color: ${Palette.CONTENT_TERTIARY};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: 0rem;
  margin: 0;

  ${({ normal }) =>
    normal &&
    `
    color: ${Palette.BLACK};
    line-height: 1.5rem;
  `}

  ${({ bold }) =>
    bold &&
    `
    line-height: 1.5rem;
    color: ${Palette.BLACK};
    font-weight: 700;
  `}

  ${({ italic }) =>
    italic &&
    `
    font-style: italic;
  `}

  ${({ alt }) =>
    alt &&
    `
    color: ${Palette.BLACK};
    font-size: 0.875rem;
    line-height: 1.5rem;
  `}
`;
